import { Editor } from "@monaco-editor/react";
import { DashboardComponentType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { ComponentTypeMappings } from "components/component-mappings";
import { IComponentTypeMapping } from "components/ComponentTypeTypes";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import MissingComponentWarning from "core/MissingComponentWarning";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import React, { Suspense, useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";

interface ComponentEditorProps {
  currentConfig: IDashboardComponent;
  setCurrentConfig: React.Dispatch<React.SetStateAction<IDashboardComponent>>;
}

const isValidComponentType = (type: DashboardComponentType): type is keyof IComponentTypeMapping => {
  return type in ComponentTypeMappings && type !== "NONE" && type !== "NOT_SUPPORTED";
};

const SCHEMAS_LOCATION = `${window.jsBasePath}/assets/generated/schemas/dashboard-components/`;

const ComponentEditor = ({ currentConfig, setCurrentConfig }: ComponentEditorProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [jsonSchema, setJsonSchema] = useState<any>(null);

  let EditorComponent;

  if (isValidComponentType(currentConfig.type)) {
    EditorComponent = ComponentTypeMappings[currentConfig.type].editorEntryPoint;
  }

  const loadSchema = async () => {
    if (isValidComponentType(currentConfig.type)) {
      try {
        const schema = await fetch(`${SCHEMAS_LOCATION}${currentConfig?.type}.json`).then(response =>
          response.json()
        );
        setJsonSchema(schema);
      } catch {
        easasAlert({ summary: T("alert.failed-to-load-schema"), severity: "error" });
      }
    }
  };

  useEffect(() => {
    loadSchema();
  }, [currentConfig?.type]);

  const handleEditorChange = (config: Partial<IDashboardComponent>) => {
    setCurrentConfig(prevState => ({
      ...prevState,
      config,
    }));
  };

  const handleJsonChange = (value?: string) => {
    try {
      const parsedValue = JSON.parse(value || "{}");
      setCurrentConfig(prevState => ({
        ...prevState,
        config: parsedValue,
      }));
    } catch (error) {
      // ignore errors while typing
    }
  };

  const onValidate = (markers: any[]) => {
    if (markers.length > 0) {
      easasAlert({
        summary: T("alert.json-is-not-valid"),
        detail: markers[0].message,
        severity: "error",
      });
    }
  };

  const handleOnMount = (monaco: any) => {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: `${SCHEMAS_LOCATION}${currentConfig?.type}`,
          fileMatch: ["*"],
          schema: jsonSchema,
        },
      ],
    });
  };

  return (
    <TabView activeIndex={activeTabIndex} onTabChange={e => setActiveTabIndex(e.index)}>
      {currentConfig && currentConfig.type && currentConfig.type !== "NONE" && (
        <TabPanel header={T("label.editor")}>
          <Suspense fallback={<ProgressSpinner />}>
            {EditorComponent ? (
              <EditorComponent config={currentConfig.config} onConfigChange={handleEditorChange} />
            ) : (
              <MissingComponentWarning componentId={currentConfig.type as string} />
            )}
          </Suspense>
        </TabPanel>
      )}
      <TabPanel header={T("label.edit-raw-json")}>
        <div className="p-fluid p-formgrid p-grid">
          <div className="p-field p-col">
            <Editor
              height="420px"
              defaultLanguage="json"
              value={JSON.stringify(currentConfig?.config, null, 2)}
              onChange={handleJsonChange}
              onValidate={onValidate}
              onMount={(editorInstance, monaco) => handleOnMount(monaco)}
              theme="vs-dark"
            />
          </div>
        </div>
      </TabPanel>
    </TabView>
  );
};

export default ComponentEditor;
